import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useWorkplaceMaster } from "../../contexts/WorkplaceMasterContext";
import Modal from "../../components/UI/Modal";
import WorkplaceForm from "../../components/Form/WorkplaceForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

const WorkplaceMasters = React.memo(() => {
  const { workplaces, addWorkplace, updateWorkplace, deleteWorkplace, isLoading } = useWorkplaceMaster();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorkplace, setEditingWorkplace] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0 });

  // Separate component for actions column to avoid hooks rule violation
  const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteApproverModule }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteApproverModule(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});


  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingWorkplace(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((workplace) => {
    setEditingWorkplace(workplace);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingWorkplace(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  // create and update function
  const handleFormSubmit = useCallback(
    async (workplace) => {
      setLoading(true);
      try {
        if (editingWorkplace) {
          // console.log("workplace payload", workplace);
        
          // Convert FormData to object
          // console.log(workplace instanceof FormData)
          const workplaceData = workplace instanceof FormData ? convertFormDataToObject(workplace) : workplace;
  
          // console.log("Payload being sent:", { ...editingWorkplace, ...workplaceData });
  
          await updateWorkplace({ ...editingWorkplace, ...workplaceData });
          setMessage("Workplace updated successfully.");
          // setNotification("Workplace updated successfully.");
          setNotification({
            message: "Workplace updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create workplace", workplace)
          await addWorkplace(workplace);
          setMessage("Workplace added successfully.");
          // setNotification("Workplace added successfully.");
          setNotification({
            message: "Workplace added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: "Unexpected error during submission:", err,
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [editingWorkplace, addWorkplace, updateWorkplace, closeModal]
  );

  // Memoize department names
  const workplaceForTable = useMemo(
    () =>
        workplaces.map((workplace) => ({
        ...workplace,
        name: workplace?.name || "Unknown Workplace",
      })),
    [workplaces]
  );

  const filteredWorkplaces = useMemo(() => {
    return workplaceForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(searchTerm);
    });
  }, [workplaceForTable, searchQuery]);

  // delete function
  const handleDeleteWorkplace = async (id) => {
    try {
      await deleteWorkplace(id);
      // setNotification("Workplace deleted successfully");
      setNotification({
        message: "Workplace deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete workplace");
      setNotification({
        message: "Failed to delete workplace",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteApproverModule={handleDeleteWorkplace}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteWorkplace,
    ]
  );
  

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Branch Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Branch
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Candidates Table */}
      <ReusableTable
        data={filteredWorkplaces}
        loading={isLoading}
        columns={columns}
        filterable={false}
        excludeFilterColumns={["actions"]}
      />

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingWorkplace ? "Edit Workplace" : "Add Workplace"}
      >
        <WorkplaceForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingWorkplace || { name: "", description: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default WorkplaceMasters;
